import { useEffect, useState, type FormEvent } from 'react'
import { useParams } from 'react-router-dom'
import {
  fetchPublicLink,
  publicTicketUrl,
  uploadToPublicLink,
  type PublicLinkInfo,
} from '../api'
import { formatDate } from '../format'

/**
 * The page a recipient lands on. No account, no session — everything it can do
 * comes from the secret already in the address bar.
 */
export default function PublicLink() {
  const { token = '' } = useParams()
  const [info, setInfo] = useState<PublicLinkInfo | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchPublicLink(token)
      .then((result) => {
        if (!cancelled) setInfo(result)
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err))
      })
    return () => {
      cancelled = true
    }
  }, [token])

  return (
    <div className="login">
      <div className="login-card">
        <h1>Arthur Storage</h1>

        {loadError && (
          <>
            <p className="error">{loadError}</p>
            <p className="muted small">
              링크가 만료되었거나 취소되었을 수 있습니다. 보낸 사람에게 다시 요청하세요.
            </p>
          </>
        )}

        {!loadError && !info && <p className="muted">확인 중…</p>}

        {info?.kind === 'upload' && <UploadPanel token={token} info={info} />}
        {info?.kind === 'download' && <DownloadPanel token={token} info={info} />}
      </div>
    </div>
  )
}

/**
 * What the link says about itself, above whichever button it carries.
 *
 * `usesLeft` is passed rather than read off `info` because the upload panel
 * counts down as it sends, and asking the hub again is not an option: a link
 * that just spent its last use answers 410, which would turn a send that
 * worked into an error on the screen.
 */
function Facts({
  info,
  usesLeft,
}: {
  info: PublicLinkInfo
  usesLeft: number | null
}) {
  return (
    <dl className="facts">
      <div>
        <dt>만료</dt>
        <dd>{info.expiresAt ? formatDate(info.expiresAt) : '없음'}</dd>
      </div>
      {usesLeft !== null && (
        <div>
          <dt>{info.kind === 'upload' ? '남은 개수' : '남은 횟수'}</dt>
          <dd>
            {usesLeft}
            {info.kind === 'upload' ? '개' : '회'}
          </dd>
        </div>
      )}
    </dl>
  )
}

function DownloadPanel({ token, info }: { token: string; info: PublicLinkInfo }) {
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [started, setStarted] = useState(false)

  async function download(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const url = await publicTicketUrl(token, password || undefined)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.rel = 'noopener'
      document.body.append(anchor)
      anchor.click()
      anchor.remove()
      setStarted(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <p className="muted small">받을 파일</p>
      <p className="mono conflict-name">{info.filename}</p>
      <Facts info={info} usesLeft={info.usesLeft} />

      <form onSubmit={(event) => void download(event)} className="stack">
        {info.hasPassword && (
          <label>
            비밀번호
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              autoFocus
            />
          </label>
        )}
        {error && <p className="error">{error}</p>}
        {started && <p className="success">다운로드를 시작했습니다.</p>}
        <button type="submit" className="primary" disabled={busy}>
          {busy ? '준비 중…' : '다운로드'}
        </button>
      </form>
    </>
  )
}

/**
 * The other direction: a stranger puts one file into a folder they cannot see.
 *
 * Deliberately shows nothing about what is already in there — the link opens a
 * folder for writing, and a listing would make it a way to read it too.
 */
function UploadPanel({ token, info }: { token: string; info: PublicLinkInfo }) {
  const [password, setPassword] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [percent, setPercent] = useState<number | null>(null)
  const [sent, setSent] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [usesLeft, setUsesLeft] = useState(info.usesLeft)
  const spent = usesLeft !== null && usesLeft <= 0

  async function send(event: FormEvent) {
    event.preventDefault()
    if (!file) return
    setPercent(0)
    setError(null)
    setSent(null)
    try {
      const handle = uploadToPublicLink({
        token,
        password: password || undefined,
        file,
        onProgress: (loaded, total) => setPercent(Math.round((loaded / total) * 100)),
      })
      await handle.promise
      setSent(file.name)
      setFile(null)
      setUsesLeft((left) => (left === null ? null : left - 1))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setPercent(null)
    }
  }

  return (
    <>
      <p className="muted small">파일을 보낼 곳</p>
      <p className="mono conflict-name">{info.filename}</p>
      <Facts info={info} usesLeft={usesLeft} />

      <form onSubmit={(event) => void send(event)} className="stack">
        {info.hasPassword && (
          <label>
            비밀번호
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>
        )}
        <label>
          보낼 파일
          <input
            type="file"
            disabled={spent}
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null)
              setSent(null)
            }}
            required
          />
        </label>
        {percent !== null && <p className="muted small">보내는 중… {percent}%</p>}
        {error && <p className="error">{error}</p>}
        {sent && <p className="success">{sent} 을(를) 보냈습니다.</p>}
        {spent && <p className="muted small">이 링크로는 더 보낼 수 없습니다.</p>}
        <button type="submit" className="primary" disabled={percent !== null || !file || spent}>
          {percent === null ? '보내기' : '보내는 중…'}
        </button>
      </form>
    </>
  )
}
